import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { movimientosPredefinidosAPI, tiposClientesAPI, tiposProveedoresAPI } from '../services/api';
import { toast } from 'react-toastify';
import '../Clientes.css';

const MovimientoPredefinidoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [soloLectura, setSoloLectura] = useState(false);
  const [tiposClientes, setTiposClientes] = useState([]);
  const [tiposProveedores, setTiposProveedores] = useState([]);
  const [formData, setFormData] = useState({
    descripcion: '',
    monto: '',
    tipo_movimiento: 'DEBITO',
    tipo_cliente_id: '',
    tipo_proveedor_id: '',
    activo: true,
    uso: 'clientes'
  });

  const loadTiposClientes = useCallback(async () => {
    try {
      const response = await tiposClientesAPI.getAll();
      if (response.success) setTiposClientes(response.data);
    } catch (error) {
      console.error('Error al cargar tipos de clientes:', error);
    }
  }, []);

  const loadTiposProveedores = useCallback(async () => {
    try {
      const response = await tiposProveedoresAPI.getAll();
      if (response.success) setTiposProveedores(response.data);
    } catch (error) {
      console.error('Error al cargar tipos de proveedores:', error);
    }
  }, []);

  const loadMovimiento = useCallback(async () => {
    try {
      const response = await movimientosPredefinidosAPI.getById(id);
      if (response.success) {
        const d = response.data;
        const desc = (d.descripcion || '').toLowerCase();
        const esSistema =
          desc.includes('abono mensual') ||
          desc === 'recibo' ||
          desc.includes('recibo de cobro') ||
          desc.includes('orden de pago') ||
          (d.uso && d.uso.toLowerCase() === 'fondos');
        setSoloLectura(!!esSistema);
        setFormData({
          ...d,
          monto: d.monto != null && d.monto !== '' ? d.monto : '',
          tipo_cliente_id: d.tipo_cliente_id || '',
          tipo_proveedor_id: d.tipo_proveedor_id || '',
          uso: d.uso === 'proveedores' ? 'proveedores' : (d.uso === 'fondos' ? 'fondos' : 'clientes')
        });
      }
    } catch (error) {
      toast.error('Error al cargar movimiento');
      navigate('/movimientos-predefinidos');
    }
  }, [id, navigate]);

  useEffect(() => {
    loadTiposClientes();
    loadTiposProveedores();
    if (id) loadMovimiento();
  }, [id, loadTiposClientes, loadTiposProveedores, loadMovimiento]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let next = value;
    if (type === 'number' && name !== 'monto') next = parseFloat(value) || 0;
    if (type === 'number' && name === 'monto') next = value === '' ? '' : (parseFloat(value) || '');
    setFormData(prev => {
      const nextData = { ...prev, [name]: type === 'checkbox' ? checked : (next !== undefined ? next : value) };
      if (name === 'uso') {
        if (value === 'proveedores') {
          nextData.tipo_cliente_id = '';
        } else if (value === 'fondos') {
          nextData.tipo_cliente_id = '';
          nextData.tipo_proveedor_id = '';
        } else {
          nextData.tipo_proveedor_id = '';
        }
      }
      return nextData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (soloLectura && id) {
      toast.info('Este movimiento predefinido es del sistema y no se puede modificar.');
      navigate('/movimientos-predefinidos');
      return;
    }
    setLoading(true);

    try {
      let response;
      if (id) {
        response = await movimientosPredefinidosAPI.update(id, formData);
      } else {
        response = await movimientosPredefinidosAPI.create(formData);
      }

      if (response.success) {
        toast.success(id ? 'Movimiento actualizado exitosamente' : 'Movimiento creado exitosamente');
        navigate('/movimientos-predefinidos');
      }
    } catch (error) {
      toast.error(error.message || 'Error al guardar movimiento');
    } finally {
      setLoading(false);
    }
  };

  const mostrarTipoCliente = formData.uso === 'clientes';
  const mostrarTipoProveedor = formData.uso === 'proveedores';
  const mostrarTipoFondos = formData.uso === 'fondos';

  return (
    <div className="cliente-form">
      <h1>{soloLectura ? 'Configuración del movimiento predefinido' : (id ? 'Editar Movimiento Predefinido' : 'Nuevo Movimiento Predefinido')}</h1>
      {soloLectura && (
        <p style={{ color: '#666', marginBottom: 16, fontSize: 14 }}>Este movimiento predefinido (Depósito/Extracción de fondos) no se puede editar ni eliminar. Solo puede ver su configuración.</p>
      )}

      <form onSubmit={handleSubmit} className="form-container">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="mov-pred-descripcion">Descripción *</label>
            <input
              id="mov-pred-descripcion"
              type="text"
              name="descripcion"
              value={formData.descripcion}
              onChange={handleChange}
              required
              placeholder="Ej: Abono mensual, Pago de factura, etc."
              readOnly={soloLectura}
              disabled={soloLectura}
              style={soloLectura ? { background: '#f5f5f5', cursor: 'default' } : {}}
            />
          </div>
          <div className="form-group">
            <label htmlFor="mov-pred-monto">Valor predeterminado (opcional)</label>
            <input
              id="mov-pred-monto"
              type="number"
              name="monto"
              value={formData.monto}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="Vacío = sin valor por defecto; al elegir esta plantilla se puede completar el monto"
              readOnly={soloLectura}
              disabled={soloLectura}
              style={soloLectura ? { background: '#f5f5f5', cursor: 'default' } : {}}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="mov-pred-tipo-mov">Tipo de Movimiento *</label>
            <select
              id="mov-pred-tipo-mov"
              name="tipo_movimiento"
              value={formData.tipo_movimiento}
              onChange={handleChange}
              required
              disabled={soloLectura}
              style={soloLectura ? { background: '#f5f5f5', cursor: 'default' } : {}}
            >
              <option value="DEBITO">Débito</option>
              <option value="CREDITO">Crédito</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="mov-pred-uso">Se aplica a *</label>
            <select
              id="mov-pred-uso"
              name="uso"
              value={formData.uso}
              onChange={handleChange}
              required
              disabled={soloLectura}
              style={soloLectura ? { background: '#f5f5f5', cursor: 'default' } : {}}
            >
              <option value="clientes">Clientes</option>
              <option value="proveedores">Proveedores</option>
              <option value="fondos">Fondos</option>
            </select>
            <small style={{ color: '#7f8c8d', marginTop: '4px', display: 'block' }}>
              {mostrarTipoFondos ? 'Movimientos de fondos (cajas / cheques de terceros)' : 'Se aplica solo a clientes o solo a proveedores'}
            </small>
          </div>
          {mostrarTipoCliente && (
            <div className="form-group">
              <label htmlFor="mov-pred-tipo-cliente">Tipo de Cliente (opcional)</label>
              <select
                id="mov-pred-tipo-cliente"
                name="tipo_cliente_id"
                value={formData.tipo_cliente_id}
                onChange={handleChange}
                disabled={soloLectura}
                style={soloLectura ? { background: '#f5f5f5', cursor: 'default' } : {}}
              >
                <option value="">Todos los tipos</option>
                {tiposClientes.map(tipo => (
                  <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
                ))}
              </select>
            </div>
          )}
          {mostrarTipoProveedor && (
            <div className="form-group">
              <label htmlFor="mov-pred-tipo-proveedor">Tipo de Proveedor (opcional)</label>
              <select
                id="mov-pred-tipo-proveedor"
                name="tipo_proveedor_id"
                value={formData.tipo_proveedor_id}
                onChange={handleChange}
                disabled={soloLectura}
                style={soloLectura ? { background: '#f5f5f5', cursor: 'default' } : {}}
              >
                <option value="">Todos los tipos</option>
                {tiposProveedores.map(tipo => (
                  <option key={tipo.id} value={tipo.id}>{tipo.nombre}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="mov-pred-activo" className="form-toggle-wrap">
            <span className="toggle-switch-box">
              <input
                id="mov-pred-activo"
                type="checkbox"
                name="activo"
                checked={formData.activo}
                onChange={handleChange}
                disabled={soloLectura}
              />
              <span className="form-toggle-switch" />
            </span>
            <span className="form-toggle-label">{formData.activo ? 'Desactivar' : 'Activar'}</span>
          </label>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/movimientos-predefinidos')} className="btn-secondary">
            {soloLectura ? 'Volver' : 'Cancelar'}
          </button>
          {!soloLectura && (
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default MovimientoPredefinidoForm;
